import axios from "axios";

const getApiUrl = () => {
  // 1. Usar variable de entorno si está definida
  if (process.env.REACT_APP_API_URL) {
    console.log(
      "✅ Usando API URL desde variable de entorno:",
      process.env.REACT_APP_API_URL
    );
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // 2. En producción
  if (
    hostname === "research.dracoder.com" ||
    hostname === "www.research.dracoder.com"
  ) {
    const apiUrl = "https://api.research.dracoder.com/api";
    console.log("✅ Detectado dominio de producción, usando:", apiUrl);
    return apiUrl;
  }

  // 3. Localhost o desarrollo
  const devUrl = "http://localhost:6001/api";
  console.log("✅ Usando URL de desarrollo:", devUrl);
  return devUrl;
};

const API_URL = getApiUrl();

// Exportar getApiUrl para uso en componentes
export { getApiUrl };

/**
 * Construye la URL completa para acceder a un archivo en el backend
 * Maneja rutas relativas como "uploads/file.pdf", "uploads/research/file.pdf", etc.
 * También maneja solo nombres de archivo (ej: "file.pdf") que se reconstruyen según el tipo.
 *
 * @param {string} relativePath - Ruta relativa del archivo o solo nombre de archivo
 * @param {string} folder - Carpeta donde está el archivo: "research", "ndas", o "" (raíz uploads). Opcional, se infiere si no se proporciona
 * @returns {string|null} URL completa del archivo o null si no hay ruta
 */
export const buildFileUrl = (relativePath, folder = null) => {
  if (!relativePath) return null;

  // Si ya es una URL completa, retornar como está
  if (
    relativePath.startsWith("http://") ||
    relativePath.startsWith("https://")
  ) {
    return relativePath;
  }

  // Normalizar backslashes a forward slashes primero
  let normalizedPath = relativePath.replace(/\\/g, "/");

  // Si la ruta ya incluye "uploads/", extraer solo el nombre del archivo para normalizarlo
  // Esto maneja el caso de fotos guardadas con ruta completa en la BD (compatibilidad hacia atrás)
  if (normalizedPath.startsWith("uploads/")) {
    // Extraer solo el nombre del archivo (última parte de la ruta)
    const pathParts = normalizedPath.split("/");
    normalizedPath = pathParts[pathParts.length - 1];
  }

  // Si solo es un nombre de archivo (sin ruta), reconstruirlo según la carpeta
  if (!normalizedPath.includes("/") && !normalizedPath.startsWith("uploads")) {
    // Si no se proporciona folder, intentar inferirlo de rutas antiguas o usar "research" por defecto
    // Si folder es "" (string vacío), usar "documents" para documentos
    if (!folder || folder === "") {
      folder = "research"; // Por defecto, archivos de proyectos están en research
    }

    // Construir la ruta completa según la carpeta
    if (folder) {
      normalizedPath = `uploads/${folder}/${normalizedPath}`;
    } else {
      normalizedPath = `uploads/${normalizedPath}`;
    }
  }

  // Limpiar cualquier duplicación de "uploads/uploads/" o "uploads/research/uploads/research/"
  // Primero limpiar duplicaciones directas de uploads/uploads/
  while (normalizedPath.includes("uploads/uploads/")) {
    normalizedPath = normalizedPath.replace(/uploads\/uploads\//g, "uploads/");
  }

  // Limpiar duplicaciones como uploads/research/uploads/research/...
  // Buscar patrones como: uploads/[carpeta]/uploads/[misma-carpeta]/
  const uploadsPattern = /uploads\/([^/]+)\/uploads\/\1\//g;
  if (uploadsPattern.test(normalizedPath)) {
    // Resetear el índice del regex y reemplazar
    uploadsPattern.lastIndex = 0;
    normalizedPath = normalizedPath.replace(uploadsPattern, "uploads/$1/");
  }

  // Limpiar duplicaciones múltiples
  while (normalizedPath.match(/uploads\/[^/]+\/uploads\//)) {
    normalizedPath = normalizedPath.replace(
      /uploads\/([^/]+)\/uploads\//g,
      "uploads/$1/"
    );
  }

  // Si es una ruta relativa que empieza con "uploads/", construir URL desde el backend
  if (normalizedPath.startsWith("uploads/")) {
    // Usar API_URL con prefijo /api/ para que pase por el proxy nginx correctamente
    // El backend tiene endpoints explícitos en /api/uploads/research/:filename, etc.
    const cleanBase = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
    // normalizedPath ya incluye "uploads/", solo agregar el slash inicial
    const cleanPath = normalizedPath.startsWith("/")
      ? normalizedPath
      : `/${normalizedPath}`;
    const finalUrl = `${cleanBase}${cleanPath}`;

    // Debug: log para verificar la construcción
    console.log("🔍 buildFileUrl:", {
      originalPath: relativePath,
      normalizedPath,
      folder,
      cleanBase,
      cleanPath,
      finalUrl,
    });

    return finalUrl;
  }

  // Si empieza con "/", quitar el slash inicial
  const cleaned = relativePath.startsWith("/")
    ? relativePath.slice(1)
    : relativePath;

  // Si no empieza con "uploads/", asumir que está en uploads/ (raíz)
  return `${API_URL}/uploads/${cleaned}`;
};

/**
 * Construir URL de imagen desde investigación (compatibilidad hacia atrás)
 * @deprecated Usar buildFileUrl en su lugar
 */
export const buildResearchImageUrl = (filePath) => {
  if (!filePath) return null;

  // Si ya es una URL completa, retornar como está
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }

  // Si empieza con "/", remover el slash inicial para normalizar
  const normalizedPath = filePath.startsWith("/")
    ? filePath.slice(1)
    : filePath;

  // Si ya tiene la ruta completa relativa (incluye "uploads/"), usar buildFileUrl directamente
  if (normalizedPath.startsWith("uploads/")) {
    // Limpiar cualquier duplicación antes de procesar
    let cleanPath = normalizedPath;
    // Limpiar duplicaciones de "uploads/uploads/"
    while (cleanPath.includes("uploads/uploads/")) {
      cleanPath = cleanPath.replace(/uploads\/uploads\//g, "uploads/");
    }

    // Verificar si hay duplicación en la estructura (ej: uploads/research/uploads/research/...)
    const pathParts = cleanPath.split("/").filter((p) => p); // Filtrar partes vacías
    if (
      pathParts.length >= 4 &&
      pathParts[0] === "uploads" &&
      pathParts[1] !== undefined &&
      pathParts[2] === "uploads" &&
      pathParts[1] === pathParts[3] // Ej: uploads/research/uploads/research
    ) {
      // Hay duplicación en la estructura, removerla
      console.warn("⚠️ Ruta duplicada detectada:", normalizedPath);
      // Mantener solo: uploads/[primerPath]/[resto]
      cleanPath = `uploads/${pathParts[1]}/${pathParts.slice(4).join("/")}`;
    }

    return buildFileUrl(cleanPath);
  }

  // Si no tiene "uploads/", asumir que es solo el nombre del archivo
  // Usar buildFileUrl con folder="research" para archivos de proyectos
  // Esto manejará tanto rutas completas como solo nombres de archivo
  return buildFileUrl(filePath, "research");
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    // Si el error es un blob (cuando responseType es "blob"), intentar parsearlo como JSON
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === "application/json"
    ) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        error.response.data = json;
      } catch (e) {
        // Si no se puede parsear, mantener el blob original
        console.error("Error parsing error response as JSON:", e);
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  refreshToken: () => api.post("/auth/refresh-token"),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    api.post("/auth/reset-password", { token, newPassword }),
};

export const documentService = {
  getAll: (params) => api.get("/documents", { params }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (formData) =>
    api.post("/documents", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (id, formData) =>
    api.put(`/documents/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  updateReview: (id, data) => api.put(`/documents/${id}/review`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  verify: (id) => api.post(`/documents/${id}/verify`),
  revoke: (id) => api.post(`/documents/${id}/revoke`),
  verifyOnChain: (id) => api.get(`/documents/${id}/verify-on-chain`),
  download: (id) =>
    api.get(`/documents/${id}/download`, {
      responseType: "blob",
      // Permitir que axios detecte automáticamente el tipo de respuesta
      transformResponse: [(data) => data],
    }),
  registerBlockchain: (id) => api.post(`/documents/${id}/register-blockchain`),
};

export const ndaService = {
  getAll: (params) => api.get("/ndas", { params }),
  getById: (id) => api.get(`/ndas/${id}`),
  getSigned: () => api.get("/ndas/signed"),
  create: (formData) =>
    api.post("/ndas", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (id, formData) =>
    api.put(`/ndas/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  delete: (id) => api.delete(`/ndas/${id}`),
  sign: (id, projectId) => api.post(`/ndas/${id}/sign`, { projectId }),
  checkExpiration: (id) => api.post(`/ndas/${id}/check-expiration`),
  verifyBlockchain: (id) => api.get(`/ndas/${id}/verify-blockchain`),
  registerBlockchain: (id) => api.post(`/ndas/${id}/register-blockchain`),
  // Endpoints para estudio jurídico
  getAllForLegal: (params) => api.get("/ndas/legal/all", { params }),
  approve: (id, comments) => api.post(`/ndas/${id}/approve`, { comments }),
  reject: (id, comments) => api.post(`/ndas/${id}/reject`, { comments }),
  // Signature-level endpoints for Legal
  getPendingSignatures: (params) =>
    api.get("/ndas/signatures/pending", { params }),
  approveSignature: (signatureId, comments) =>
    api.post(`/ndas/signatures/${signatureId}/approve`, { comments }),
  rejectSignature: (signatureId, comments) =>
    api.post(`/ndas/signatures/${signatureId}/reject`, { comments }),
  // Signature file download/upload endpoints
  downloadTemplate: (signatureId) =>
    api.get(`/ndas/signatures/${signatureId}/download-template`, {
      responseType: "blob",
    }),
  uploadCompletedNda: (signatureId, formData) =>
    api.post(`/ndas/signatures/${signatureId}/upload-completed`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  downloadUserUpload: (signatureId) =>
    api.get(`/ndas/signatures/${signatureId}/download-user-upload`, {
      responseType: "blob",
    }),
  uploadLegalSigned: (signatureId, formData) =>
    api.post(`/ndas/signatures/${signatureId}/upload-legal-signed`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  downloadLegalUpload: (signatureId) =>
    api.get(`/ndas/signatures/${signatureId}/download-legal-upload`, {
      responseType: "blob",
    }),
};

export const ndaTypeService = {
  getAll: (params) => api.get("/nda-types", { params }),
  getById: (id) => api.get(`/nda-types/${id}`),
  create: (formData) =>
    api.post("/nda-types", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (id, formData) =>
    api.put(`/nda-types/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  delete: (id) => api.delete(`/nda-types/${id}`),
};

export const certificateService = {
  getAll: (params) => api.get("/certificates", { params }),
  getById: (id) => api.get(`/certificates/${id}`),
  create: (formData) =>
    api.post("/certificates", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (id, data) => api.put(`/certificates/${id}`, data),
  delete: (id) => api.delete(`/certificates/${id}`),
  revoke: (id) => api.post(`/certificates/${id}/revoke`),
  getQRCode: (id) =>
    api.get(`/certificates/${id}/qr-code`, { responseType: "blob" }),
  verifyPublic: (id) => api.get(`/certificates/${id}/verify/public`),
  verifyOnChain: (id) => api.get(`/certificates/${id}/verify-on-chain`),
};

export const userService = {
  getAll: (params) => api.get("/users", { params }),
  getStudents: () => api.get("/users/students"),
  getPendingUsers: () => api.get("/users/pending"),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  approveUser: (id, data) => api.post(`/users/${id}/approve`, data),
  rejectUser: (id, data) => api.post(`/users/${id}/reject`, data),
  getProfile: () => api.get("/users/profile"),
  updateProfile: (data) => api.put("/users/profile", data),
  updatePhoto: (formData) =>
    api.put("/users/profile/photo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  updatePassword: (id, data) => api.put(`/users/${id}/password`, data),
};

export const researcherService = {
  getAll: (params) => api.get("/researchers", { params }),
  getById: (id) => api.get(`/researchers/${id}`),
  create: (data) => api.post("/researchers", data),
  update: (id, data) => api.put(`/researchers/${id}`, data),
  delete: (id) => api.delete(`/researchers/${id}`),
};

export const researchProjectService = {
  getAll: (params) => api.get("/research-projects", { params }),
  getById: (id) => api.get(`/research-projects/${id}`),
  getFiles: (id) => api.get(`/research-projects/${id}/files`),
  signNDA: (id) => api.post(`/research-projects/${id}/sign-nda`),
  getNDAStatus: (id) => api.get(`/research-projects/${id}/nda-status`),
  create: (formData) =>
    api.post("/research-projects", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (id, formData) =>
    api.put(`/research-projects/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  delete: (id) => api.delete(`/research-projects/${id}`),
  search: (params) => api.get("/research-projects/search/public", { params }),
  registerBlockchain: (id) =>
    api.post(`/research-projects/${id}/register-blockchain`),
  getAllWithNDAs: () => api.get("/research-projects/admin/all-with-ndas"),
};

export const projectInvitationService = {
  inviteUser: (projectId, email) =>
    api.post(`/invitations/projects/${projectId}/invite`, { email }),
  acceptInvitation: (projectId, token) =>
    api.get(`/invitations/projects/${projectId}/accept-invitation`, {
      params: { token },
    }),
};

export const notificationService = {
  getAll: (params) => api.get("/notifications", { params }),
  getUnreadCount: () => api.get("/notifications/unread-count"),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put("/notifications/read-all"),
};

export default api;
